<template>
  <div
    ref="heroRoot"
    class="gr-hero"
    @focusin="isHeroFocused = true"
    @focusout="onHeroFocusOut"
  >
    <Carousel
      v-if="banners.length > 0"
      :items-to-show="1"
      :items-to-scroll="1"
      :autoplay="shouldAutoplay ? HERO_AUTOPLAY_MS : 0"
      :transition="HERO_TRANSITION_MS"
      slide-effect="fade"
      :wrap-around="banners.length > 1"
      :mouse-drag="banners.length > 1"
      :touch-drag="banners.length > 1"
      class="gr-hero__carousel"
    >
      <Slide
        v-for="(banner, index) in banners"
        :key="banner.uuid"
        :data-index="index"
      >
        <!-- Only the first slide is above the fold, and only its URL has to be
             in the server-rendered document. Loading the rest eagerly holds the
             window load event open behind images nobody has scrolled to yet;
             `loading` does not affect what SSR emits. -->
        <img
          v-if="banner.imageUrl && !brokenBannerUuids.has(banner.uuid)"
          :src="banner.imageUrl"
          :alt="banner.altText"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          class="gr-hero__image"
          @error="brokenBannerUuids.add(banner.uuid)"
        />
        <!-- Neutral surface when the API image fails: no broken-image glyph
             and no technical message reaches the visitor. -->
        <div
          v-else
          class="gr-hero__image gr-hero__image--fallback"
          role="img"
          :aria-label="banner.altText"
        />
      </Slide>
      <template #addons>
        <template v-if="banners.length > 1">
          <Navigation />
          <Pagination />
        </template>
      </template>
    </Carousel>

    <!-- The pause control stays outside <Slide>: focusing inside a slide calls
         slideTo, which would move the hero as soon as the visitor tabs to it.
         isPaused is persistent — only another press of this button clears it. -->
    <button
      v-if="banners.length > 1"
      type="button"
      class="gr-hero__pause"
      :aria-label="isPaused ? 'Putar rotasi banner' : 'Jeda rotasi banner'"
      :aria-pressed="isPaused"
      @click="isPaused = !isPaused"
    >
      <v-icon
        :icon="isPaused ? 'mdi-play' : 'mdi-pause'"
        size="20"
        aria-hidden="true"
      />
    </button>

    <div class="gr-hero__scrim">
      <div class="gr-container gr-hero__inner">
        <p class="gr-hero__eyebrow">Gotong Royong Indonesia</p>
        <h1 class="gr-hero__title">
          <span class="gr-hero__title-lead">DUNIA BUTUH</span>
          SENTUHAN KITA
        </h1>
        <p class="gr-hero__copy">
          Jadilah bagian dari aksi sosial positif yang langsung berdampak pada
          masyarakat secara modern, transparan, dan bertumbuh.
        </p>
        <NuxtLink
          v-if="!$auth.isAuthenticated"
          to="/auth?type=signup"
          class="gr-btn gr-btn--solid gr-hero__cta"
        >
          Bergabung Sekarang
          <v-icon icon="mdi-arrow-right" size="18" aria-hidden="true" />
        </NuxtLink>
        <NuxtLink
          v-else
          to="/activities?status=ON_REGISTRATION"
          class="gr-btn gr-btn--solid gr-hero__cta"
        >
          Cari aktivitas
          <v-icon icon="mdi-arrow-right" size="18" aria-hidden="true" />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import "vue3-carousel/carousel.css";
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import { usePreferredReducedMotion } from "@vueuse/core";
import { useSiteBanners } from "~/features/home/composables/useSiteBanners";

const HERO_AUTOPLAY_MS = 5000;
const HERO_TRANSITION_MS = 400;

// Read-only shared composable, reused rather than reimplemented. It already
// swallows a failed request into an empty list so the hero paints its bundled
// fallback background instead of an error.
const { data } = useSiteBanners();
const banners = computed(() => data.value ?? []);

const brokenBannerUuids = reactive(new Set<string>());

// Persistent, visitor-controlled pause. Never auto-resumed.
const isPaused = ref(false);

// SSR-safe: vueuse reports "no-preference" on the server and evaluates
// matchMedia on the client. When "reduce", autoplay is 0 and the carousel keeps
// working manual controls.
const preferredMotion = usePreferredReducedMotion();
const isReducedMotion = computed(() => preferredMotion.value === "reduce");

// Keyboard focus anywhere inside the hero suspends rotation. focusout only
// resumes when focus actually leaves the hero, so tabbing between the arrows,
// pagination, pause, and CTA never flickers autoplay.
const heroRoot = ref<HTMLElement | null>(null);
const isHeroFocused = ref(false);

const onHeroFocusOut = (event: FocusEvent): void => {
  const next = event.relatedTarget;
  if (next instanceof Node && heroRoot.value?.contains(next)) return;
  isHeroFocused.value = false;
};

const shouldAutoplay = computed(
  () =>
    banners.value.length > 1 &&
    !isPaused.value &&
    !isReducedMotion.value &&
    !isHeroFocused.value,
);
</script>

<style>
/* Not scoped: the vue3-carousel addons render outside this component's
   template. Every selector is prefixed with the candidate hero class. */
.gr-hero {
  position: relative;
  background-image: url("/assets/images/banner-header.jpeg");
  background-position: center;
  background-size: cover;
  /* Locked by spec 002 plan §2 at 0.55 minimum; 0.6 keeps a rounding margin. */
  --gr-hero-scrim: rgba(0, 0, 0, 0.6);
}

.gr-hero__carousel {
  position: absolute;
  inset: 0;
  height: 100%;
  /* Root stays non-stacking so slides paint under the scrim while the
     positive z-index controls rise above it. */
  z-index: auto;
}

.gr-hero__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.gr-hero__image--fallback {
  background-color: #e0e0e0;
}

.gr-hero__scrim {
  position: relative;
  z-index: 1;
  background-color: var(--gr-hero-scrim);
  pointer-events: none;
}

.gr-hero__inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: clamp(420px, 62vh, 620px);
  max-width: 1200px;
  padding-block: clamp(3rem, 2rem + 4vw, 5.5rem);
}

.gr-hero__eyebrow {
  margin: 0 0 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.42);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 1;
  text-transform: uppercase;
}

.gr-hero__title {
  margin: 0;
  max-width: 16ch;
  color: #ffffff;
  /* The floor is 1.5rem, not 2.1rem: at an effective 188 CSS px (375 px at
     200% zoom) "SENTUHAN" is wider than the container at the larger size. */
  font-size: clamp(1.5rem, 1rem + 4.2vw, 3.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.06;
  overflow-wrap: anywhere;
}

.gr-hero__title-lead {
  display: block;
}

.gr-hero__copy {
  max-width: 46ch;
  margin: 18px 0 0;
  color: rgba(255, 255, 255, 0.94);
  font-size: clamp(1rem, 0.95rem + 0.3vw, 1.125rem);
  line-height: 1.6;
}

.gr-hero__cta {
  margin-top: 28px;
  border-color: rgba(255, 255, 255, 0.9);
  pointer-events: auto;
}

/* --- carousel controls ------------------------------------------------ */

.gr-hero__carousel .carousel__prev,
.gr-hero__carousel .carousel__next,
.gr-hero__carousel .carousel__pagination {
  z-index: 2;
}

/* Recessive at rest, definite on approach. The controls sit on top of the
   0.6 scrim, so a plain translucent disc keeps the white glyph well past 3:1
   without the boxed-in border and shadow the first draft used. */
.gr-hero__carousel .carousel__prev,
.gr-hero__carousel .carousel__next {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 9999px;
  background-color: rgba(0, 0, 0, 0.5);
  box-shadow: none;
  color: #fff;
  /* 0.72, not lower: over the brightest hero photography the white glyph still
     measures about 3.4:1 against its own disc at this value. */
  opacity: 0.72;
  transition:
    opacity 180ms cubic-bezier(0.2, 0, 0, 1),
    background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

.gr-hero__carousel .carousel__icon {
  width: 20px;
  height: 20px;
}

.gr-hero__carousel .carousel__prev {
  inset-inline-start: 12px;
}

.gr-hero__carousel .carousel__next {
  inset-inline-end: 12px;
}

/* Any pointer over the hero, or any keyboard focus inside it, brings the whole
   control set up to full strength together. */
.gr-hero:hover .carousel__prev,
.gr-hero:hover .carousel__next,
.gr-hero:focus-within .carousel__prev,
.gr-hero:focus-within .carousel__next {
  opacity: 1;
}

@media (hover: hover) {
  .gr-hero__carousel .carousel__prev:hover,
  .gr-hero__carousel .carousel__next:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
}

/* Coarse pointers never hover, so the controls stay at full strength there. */
@media (hover: none) {
  .gr-hero__carousel .carousel__prev,
  .gr-hero__carousel .carousel__next {
    opacity: 1;
  }
}

.gr-hero__carousel .carousel__prev:focus-visible,
.gr-hero__carousel .carousel__next:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 2px;
  background-color: #1b5e20;
}

/* Active indicator differs by shape and width, not colour alone. The 44px hit
   area is kept; only the painted mark is small. */
.gr-hero__carousel .carousel__pagination {
  bottom: 16px;
  gap: 0;
  height: 32px;
  padding-inline: 4px;
  border-radius: 9999px;
  /* A known dark bed under the marks: without it the inactive dash drops to
     about 2:1 over a bright frame. */
  background-color: rgba(0, 0, 0, 0.35);
}

.gr-hero__carousel .carousel__pagination-button {
  position: relative;
  display: grid;
  place-items: center;
  width: 36px;
  height: 32px;
  padding: 0;
  background: transparent;
  border-radius: 9999px;
}

.gr-hero__carousel .carousel__pagination-button::before {
  width: 8px;
  height: 3px;
  border-radius: 0;
  background-color: rgba(255, 255, 255, 0.7);
  content: "";
  transition:
    width 180ms cubic-bezier(0.2, 0, 0, 1),
    background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

.gr-hero__carousel .carousel__pagination-button--active::before {
  width: 20px;
  height: 3px;
  background-color: #ffffff;
}

@media (hover: hover) {
  .gr-hero__carousel .carousel__pagination-button:hover::before {
    background-color: #ffffff;
  }
}

.gr-hero__carousel .carousel__pagination-button:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: 1px;
}

/* --- pause control ---------------------------------------------------- */

/* Icon only, same disc as the arrows, on the same bottom band as the
   indicators. The accessible name still says whether it will pause or play,
   and aria-pressed still carries the state. */
.gr-hero__pause {
  position: absolute;
  right: 12px;
  bottom: 14px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background-color: rgba(0, 0, 0, 0.3);
  box-shadow: none;
  color: #fff;
  font: inherit;
  line-height: 1;
  cursor: pointer;
  opacity: 0.72;
  transition:
    opacity 180ms cubic-bezier(0.2, 0, 0, 1),
    background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

.gr-hero:hover .gr-hero__pause,
.gr-hero:focus-within .gr-hero__pause,
.gr-hero__pause[aria-pressed="true"] {
  opacity: 1;
}

@media (hover: none) {
  .gr-hero__pause {
    opacity: 1;
  }
}

@media (hover: hover) {
  .gr-hero__pause:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
}

@media (max-width: 599px) {
  /* Vertically centred arrows land on top of the hero paragraph at this width,
     so the whole control set moves down to one band along the bottom edge:
     previous, indicators, next, pause. */
  .gr-hero__carousel .carousel__prev,
  .gr-hero__carousel .carousel__next {
    top: auto;
    bottom: 14px;
    transform: none;
  }

  .gr-hero__carousel .carousel__prev {
    inset-inline-start: 6px;
  }

  .gr-hero__carousel .carousel__next {
    inset-inline-end: 54px;
  }

  .gr-hero__pause {
    right: 6px;
  }

  /* Clears that band. */
  .gr-hero__inner {
    padding-bottom: 5.5rem;
  }
}
</style>
